/**
 * Horizontal bars for funnels, countries and top events.
 * The raw event name stays in the tooltip and in the CSV.
 */
import { el } from '../../core/dom.js';
import { button } from '../button.js';
import { csvFilename, csvMatrix, downloadCsv } from './csv.js';
import { isUnverified, UNVERIFIED_TITLE } from './kpi-figure.js';
import { COLLAPSE_AFTER, askAboutRow, cellRaw, cellText, resolvedOf } from './shape.js';

function labelOf(item) {
  if (item && item.label && typeof item.label === 'object') return cellText(item.label);
  return item && item.label != null ? String(item.label) : '';
}

function rawOf(item) {
  if (item && item.label && typeof item.label === 'object') return cellRaw(item.label);
  return item && item.raw ? String(item.raw) : '';
}

function widthOf(item, max) {
  if (typeof item.percent === 'number' && Number.isFinite(item.percent)) {
    return Math.max(0, Math.min(100, item.percent));
  }
  const value = Number(item.value);
  if (!Number.isFinite(value) || !max) return 0;
  return Math.max(0, Math.min(100, (value / max) * 100));
}

function rowRecord(item) {
  return {
    label: item.label && typeof item.label === 'object' ? item.label : { text: labelOf(item), raw: rawOf(item) },
    value: { text: item.valueText != null ? String(item.valueText) : String(item.value ?? '') },
    dropoff: { text: item.dropoff ? String(item.dropoff) : '' },
  };
}

/**
 * @param {object} block
 * @param {{ onAsk?: Function }} [actions]
 * @returns {HTMLElement}
 */
export function renderBarList(block, actions) {
  const data = resolvedOf(block);
  const items = Array.isArray(data.items) ? data.items : [];
  const title = data.title ? String(data.title) : '';
  const max = items.reduce((best, item) => Math.max(best, Number(item.value) || 0), 0);
  const groups = [];
  let open = false;
  const list = el('div', { class: 'chat-bars', role: 'list' });
  const more = el('button', { type: 'button', class: 'chat-more' });

  function paint() {
    const shown = open ? items : items.slice(0, COLLAPSE_AFTER);
    list.replaceChildren(...shown.map((item, index) => {
      const label = labelOf(item);
      const raw = rawOf(item);
      const group = item.group ? String(item.group) : '';
      if (group && !groups.includes(group)) groups.push(group);
      const slot = group ? groups.indexOf(group) % 8 : index % 8;
      const tip = raw && raw !== label ? `${label} (${raw})` : label;
      return el('div', { class: 'chat-bar', role: 'listitem' },
        el('button', {
          type: 'button',
          class: 'chat-bar-label',
          title: tip,
          onClick: () => {
            if (actions && actions.onAsk) actions.onAsk(askAboutRow(title, label));
          },
        }, label),
        el('span', { class: 'chat-bar-track', 'aria-hidden': 'true' },
          el('span', { class: `chat-bar-fill is-c${slot}`, style: `width:${widthOf(item, max)}%` })),
        el('span', {
          class: isUnverified(item) ? 'chat-bar-value is-unverified' : 'chat-bar-value',
          title: isUnverified(item) ? UNVERIFIED_TITLE : null,
        },
          isUnverified(item)
            ? el('span', { class: 'chat-unverified', title: UNVERIFIED_TITLE },
              item.valueText != null ? String(item.valueText) : (item.text != null ? String(item.text) : String(item.value ?? '')))
            : (item.valueText != null ? String(item.valueText) : (item.text != null ? String(item.text) : String(item.value ?? ''))),
          item.dropoff ? el('span', { class: 'chat-drop' }, ` ${item.dropoff}`) : null));
    }));
    const extra = Math.max(0, items.length - COLLAPSE_AFTER);
    if (extra === 0) more.setAttribute('hidden', '');
    else more.removeAttribute('hidden');
    more.setAttribute('aria-expanded', open ? 'true' : 'false');
    more.textContent = open ? 'Show fewer rows' : `Show ${extra} more rows`;
  }

  more.addEventListener('click', () => {
    open = !open;
    paint();
  });
  paint();
  const columns = [
    { key: 'label', label: 'Label' },
    { key: 'value', label: 'Value' },
    { key: 'dropoff', label: 'Drop-off' },
  ];
  const tools = el('div', { class: 'chat-block-tools' },
    title ? el('h3', { class: 'chat-block-title' }, title) : null,
    button('Download CSV', {
      className: 'chat-tool',
      onClick: () => downloadCsv(csvFilename(title || 'bar-list'), csvMatrix(columns, items.map(rowRecord))),
    }));
  return el('section', {
    class: 'chat-block chat-block-bar-list',
    'data-block-type': 'bar-list',
    'data-block-id': (block && block.id) || '',
    'aria-label': title || 'Bar list',
  }, tools, list, more);
}
